import React, { ReactNode, useCallback, useState } from 'react';
import { Text, Stack, Form } from 'rsuite';
import FormFieldErrorIcon from './FormFieldErrorIcon';
import {
  RadioGroupWithErrorStatus,
  RadioGroupWithErrorStatusProps,
} from './RadioGroupWithErrorStatus';

export interface RadioQuestionGroupProps {
  name: string;
  title?: string | ReactNode;
  description?: string | ReactNode;
  options: any[];
  defaultOption: any;
  onChange: (value: any) => void;
  hasError: boolean;
  validationRule: any;
  errorMessage?: string;
  hideErrorIcon?: boolean;
  disabled?: boolean;
  required?: boolean;
  value?: any;
  className?: string;
}

const RadioQuestionGroup = ({
  name,
  title = '',
  description = '',
  options,
  defaultOption,
  onChange,
  hasError,
  validationRule,
  errorMessage = '',
  hideErrorIcon = false,
  disabled = false,
  required,
  ...rest
}: RadioQuestionGroupProps) => {
  const [isChanged, setIsChanged] = useState(false);

  const changedInternal = (v: any) => {
    setIsChanged(true);
    onChange(v);
  };

  const errorId = hasError && name ? `${name}-error` : undefined;

  const RadioGroupWithErrorStateAndProps = useCallback(
    (props: RadioGroupWithErrorStatusProps) => (
      <RadioGroupWithErrorStatus
        {...props}
        ariaInvalid={hasError || undefined}
        ariaDescribedby={errorId}
        options={options}
        defaultOption={defaultOption}
        onChange={changedInternal}
        disabled={disabled}
        legend={title || undefined}
      />
    ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      hasError,
      errorId,
      errorMessage,
      hideErrorIcon,
      defaultOption,
      options,
      title,
    ]
  );

  return (
    <>
      <Stack
        direction="row"
        {...rest}
      >
        <Stack
          direction="column"
          alignItems="flex-start"
        >
          {title && (
            <Text
              as="h5"
              className="text-bold"
            >
              {title}
              {required && ' *'}
            </Text>
          )}
          {description && <Text>{description}</Text>}
          <Form.Control
            name={name}
            rule={validationRule}
            errorMessage={errorMessage}
            accepter={RadioGroupWithErrorStateAndProps}
            {...rest}
            disabled={disabled}
          />
          {hasError && errorMessage && (
            <span
              id={`${name}-error`}
              className="text-error"
            >
              {errorMessage}
            </span>
          )}
        </Stack>
        {hasError && !isChanged && (
          <FormFieldErrorIcon errorMessage={errorMessage} />
        )}
      </Stack>
    </>
  );
};

export default RadioQuestionGroup;
